// File is outside locales dir due to https://github.com/nuxt-modules/i18n/issues/2145
import { LocalesError } from '@utils/errors';
import { isObjectEmpty } from '@utils/object';
import { useErrorLogger } from '@composables/useErrorLogger';
import type { AvailableLocalesCode } from '@utils/availableLocales';

const { errorLoggerCaptureException } = useErrorLogger();

export type Messages = Record<string, string>;
export type MessagesResponse = { messages: Messages };

export const importDefaultFile = async () =>
    await import('@utils/default.json');

export const loadDefaultTranslations = async (): Promise<Messages> => {
    try {
        const defaultTranslations = await importDefaultFile();
        return defaultTranslations.default.messages;
    } catch (error) {
        errorLoggerCaptureException(
            new LocalesError('Failed to load default locales')
        );
        return {};
    }
};

export const loadTranslations = async (
    locale: AvailableLocalesCode
): Promise<Messages> => {
    const config = useRuntimeConfig();
    try {
        const { messages } = await $fetch<MessagesResponse>(
            config.public.translationCdnUrl,
            { query: { locale } }
        );
        if (isObjectEmpty(messages)) {
            errorLoggerCaptureException(
                new LocalesError('Request returned no messages'),
                {
                    extra: { locale, messages },
                }
            );
            return await loadDefaultTranslations();
        }
        return messages;
    } catch (error) {
        errorLoggerCaptureException(
            new LocalesError('Failed to fetch messages'),
            {
                extra: { locale },
            }
        );
        return await loadDefaultTranslations();
    }
};
